import React, { useEffect, useState } from 'react'
import {
  Modal,
  Space,
  useModalContext,
  Button,
  ButtonGroup,
  get,
  useTriggerSnackbar,
} from '@ally/metronome-ui'
import { Forms, useFormsContext } from '@ally/forms'
import styled from 'styled-components'
import allytm from '@ally/allytm'
import { useAccountDetailsApi } from '../../../api/account-details-api'
import { AccountDetails } from '../../../types'
import { OwnershipTypeField } from './OwnershipTypeField'
import { useNonIraBeneficiaries } from '../../../services/NonIraBeneficiariesProvider'

interface FormValues {
  ownershipType: string | undefined
}

const StyledButtonGroup = styled(ButtonGroup)`
  margin-top: ${get.space('md')};
  margin-bottom: ${get.space('sm')};
`
const AddButton: React.FC<{
  accountId: string
  accountLabel: string
}> = ({ accountId, accountLabel }) => {
  const { setOpen } = useModalContext()
  return (
    <Button
      allytmln={`edit-account-ownership-${accountLabel}`}
      onClick={(): void => setOpen(true)}
      text="Edit"
      variant="link"
      id={`${accountId}-edit-button`}
    />
  )
}

export const AccountOwnershipTypeModalFormInner: React.FC<{
  isLoading: boolean
  showErrorMessage: boolean
  setShowErrorMessage: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ isLoading, showErrorMessage, setShowErrorMessage }) => {
  const { submitForm } = useFormsContext()
  const { setOpen } = useModalContext()
  return (
    <Space mb="md">
      <OwnershipTypeField modalView showErrorMessage={showErrorMessage} />
      <StyledButtonGroup>
        <Button
          allytmln="Save Non IRA Account Ownership Change"
          text="Save"
          loading={isLoading}
          variant="primary"
          onClick={submitForm}
        />
        <Button
          allytmln="Cancel Non IRA Account Ownership Change"
          variant="link"
          text="Cancel"
          data-testid="ownershipTypeModalCancel"
          onClick={(): void => {
            setOpen(false)
            setShowErrorMessage(false)
          }}
        />
      </StyledButtonGroup>
    </Space>
  )
}
export const ModalContent: React.FC<{
  account: AccountDetails
}> = ({ account }) => {
  const { updateAccountDetails, updateCdAccountDetails } =
    useAccountDetailsApi()
  const [isLoading, setIsLoading] = useState(false)
  const { triggerSnackbar } = useTriggerSnackbar()
  const [showErrorMessage, setShowErrorMessage] = useState(false)
  const { setOpen } = useModalContext()
  const { getBeneficiaries } = useNonIraBeneficiaries()
  const handleSubmit = async (values: FormValues): Promise<void> => {
    const { ownershipType } = values

    if (ownershipType === account.distributionType) {
      setOpen(false)
    } else
      try {
        setShowErrorMessage(false)
        setIsLoading(true)
        if (account.accountType === 'CDA') {
          await updateCdAccountDetails(account.accountId, {
            distributionType:
              ownershipType || // istanbul ignore next // low value
              '',
          })
        }
        await updateAccountDetails(account.accountId, {
          distributionType:
            ownershipType || // istanbul ignore next // low value
            '',
        })
        await getBeneficiaries()
        // istanbul ignore next // we already have a test for changing account ownership type
        triggerSnackbar({
          text: 'You updated the account ownership type for your account.',
          columnSize: 4,
          snackbarButtonText: 'Close',
          onClose: () =>
            requestAnimationFrame(() => {
              document
                .getElementById(`${account.accountId}-edit-button`)
                ?.focus()
            }),
        })
        // istanbul ignore next - tagging
        allytm.event('customEvent', {
          name: 'formSuccess',
          type: 'formValidationSuccess',
          attr: {
            formValues: {
              form: 'Update Non IRA Account Ownership Type',
              values: [],
            },
          },
        })
      } catch (e) {
        setShowErrorMessage(true)
        allytm.event(
          'customError',
          `We couldn't process your request. Please contact Customer Service at 1-877-247-2559.`,
          '',
        )
      } finally {
        setIsLoading(false)
      }
  }

  useEffect(() => {
    allytm.event(
      'modalView',
      'AOS:Beneficiaries:Bank Checking Savings and CDs:Account Ownership Type',
      {
        treatAsPageview: true,
      },
    )
  }, [])

  return (
    <Forms
      formId="account-ownership-type"
      initialValues={{ ownershipType: account.distributionType }}
      onSubmit={handleSubmit}
    >
      <AccountOwnershipTypeModalFormInner
        isLoading={isLoading}
        showErrorMessage={showErrorMessage}
        setShowErrorMessage={setShowErrorMessage}
      />
    </Forms>
  )
}

export const AccountOwnershipTypeModal: React.FC<{
  account: AccountDetails
  accountLabel: string
}> = ({ account, accountLabel }) => {
  return (
    <Modal
      trigger={
        <AddButton accountId={account.accountId} accountLabel={accountLabel} />
      }
      content={<ModalContent account={account} />}
      fullScreenMd
      width="sm"
    />
  )
}
